import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Icon } from "semantic-ui-react";
import api from "../../services/api/api";
import { api_url } from "../../config/environment/environmentSettings";
import { getToken } from "../../services/authentication/authentication";
import "./ModalClientDocuments.css";

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp"];

const normalizeExtension = (extension) => {
  if (!extension) return "";
  const ext = extension.toLowerCase();
  return ext.startsWith(".") ? ext : `.${ext}`;
};

const isPdf = (file) => {
  const ext = normalizeExtension(file.extension);
  return ext === ".pdf" || (file.mimeType || "").includes("pdf");
};

const isImage = (file) => {
  const ext = normalizeExtension(file.extension);
  if (IMAGE_EXTENSIONS.includes(ext)) return true;
  return (file.mimeType || "").startsWith("image/");
};

const ModalClientDocuments = ({ openModal, handleClose, demandId }) => {
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  const fetchDocuments = useCallback(
    async (folderId = null) => {
      if (!demandId) return;
      setLoading(true);
      try {
        const params = folderId ? { folderId } : {};
        const response = await api.get(`/demands/${demandId}/client-documents`, {
          params,
        });
        setFolders(response.data.folders || []);
        setFiles(response.data.files || []);
      } catch (error) {
        setFolders([]);
        setFiles([]);
      } finally {
        setLoading(false);
      }
    },
    [demandId]
  );

  useEffect(() => {
    if (openModal) {
      setCurrentFolder(null);
      setPreviewFile(null);
      fetchDocuments();
    }
  }, [openModal, fetchDocuments]);

  const handleOpenFolder = (folder) => {
    setCurrentFolder(folder);
    setPreviewFile(null);
    fetchDocuments(folder._id);
  };

  const handleBackToRoot = () => {
    setCurrentFolder(null);
    setPreviewFile(null);
    fetchDocuments();
  };

  const fileUrl = (file, disposition) => {
    return `${api_url}/demands/${demandId}/client-documents/${file._id}?disposition=${disposition}&token=${getToken()}`;
  };

  const handleFileClick = (file) => {
    if (isPdf(file) || isImage(file)) {
      setPreviewFile(file);
      return;
    }
    window.open(fileUrl(file, "attachment"), "_blank");
  };

  const handleCloseModal = () => {
    setCurrentFolder(null);
    setPreviewFile(null);
    handleClose();
  };

  const emptyMessage =
    !loading && folders.length === 0 && files.length === 0
      ? "Este cliente ainda não possui documentos no repositório."
      : null;

  return (
    <Modal open={openModal} onClose={handleCloseModal} size="large">
      <Modal.Header>Documentos do cliente</Modal.Header>
      <Modal.Content scrolling>
        {previewFile ? (
          <div className="client-documents-preview">
            <button
              type="button"
              className="client-documents-back"
              onClick={() => setPreviewFile(null)}
            >
              <Icon name="arrow left" /> Voltar à lista
            </button>
            <p className="client-documents-preview-title">{previewFile.name}</p>
            {isPdf(previewFile) && (
              <iframe
                title={previewFile.name}
                src={fileUrl(previewFile, "inline")}
                className="client-documents-iframe"
              />
            )}
            {isImage(previewFile) && !isPdf(previewFile) && (
              <img
                alt={previewFile.name}
                src={fileUrl(previewFile, "inline")}
                className="client-documents-image"
              />
            )}
          </div>
        ) : (
          <>
            {currentFolder && (
              <button
                type="button"
                className="client-documents-back"
                onClick={handleBackToRoot}
              >
                <Icon name="arrow left" /> Voltar
              </button>
            )}
            {loading && <p>Carregando documentos...</p>}
            {emptyMessage && <p>{emptyMessage}</p>}
            {!loading && (
              <ul className="client-documents-list">
                {folders.map((folder) => (
                  <li key={folder._id}>
                    <button
                      type="button"
                      className="client-documents-item folder"
                      onClick={() => handleOpenFolder(folder)}
                    >
                      <Icon name="folder" /> {folder.name}
                    </button>
                  </li>
                ))}
                {files.map((file) => (
                  <li key={file._id}>
                    <button
                      type="button"
                      className="client-documents-item file"
                      onClick={() => handleFileClick(file)}
                    >
                      <Icon name="file outline" /> {file.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleCloseModal}>Fechar</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalClientDocuments;
